import { Box, SxProps, Theme, Typography } from "@mui/material";
import Error from "Components/Error";
import Loading from "Components/Loading/Loading";
import { AnimatePresence, motion } from "motion/react";
import { ReactNode } from "react";

export interface IAnalyticsChartAreaProps {
  children?: ReactNode;
  loading?: boolean;
  error?: boolean;
  /** When set, replaces the chart area with an empty state message or node. */
  empty?: ReactNode;
  /** Wraps children in the graph height shell used by composed charts. */
  graphLayout?: boolean;
  /**
   * Slide/fade content in via AnimatePresence. Off by default — mode="wait"
   * plus Loading exit can leave graphs stuck on the spinner after data arrives.
   */
  animated?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * UI shell for analytics chart content inside AnalyticsCard / AnalyticsChart.
 * Handles loading/error/empty states, optional motion wrapper, and graph sizing.
 */
function AnalyticsChartArea({
  children,
  loading,
  error,
  empty,
  graphLayout = false,
  animated = false,
  sx,
}: IAnalyticsChartAreaProps) {
  const motionBoxSx: SxProps<Theme> = {
    flex: "1 1 auto",
    minHeight: graphLayout ? 0 : "min-content",
    maxWidth: "100%",
    width: "100%",
    position: "relative",
    ...(graphLayout
      ? { display: "flex", flexDirection: "column", height: "100%" }
      : {}),
    ...sx,
  };

  const content = graphLayout ? (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "calc(100% - 5px)",
        width: "100%",
      }}
    >
      {children}
    </Box>
  ) : (
    children
  );

  if (loading) {
    return <Loading key="loading" sx={{ height: "calc(100% - 15px)" }} />;
  }

  if (error) {
    return (
      <Box key="error" sx={{ height: "calc(100% - 15px)", width: "100%" }}>
        <Error />
      </Box>
    );
  }

  if (empty != null && empty !== false) {
    return (
      <Box
        key="empty"
        sx={{
          height: "calc(100% - 15px)",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {typeof empty === "string" ? (
          <Typography color="text.secondary">{empty}</Typography>
        ) : (
          empty
        )}
      </Box>
    );
  }

  if (!animated) {
    return (
      <Box key="content" sx={motionBoxSx}>
        {content}
      </Box>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <Box
        key="content"
        component={motion.div}
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -300, opacity: 0 }}
        sx={motionBoxSx}
      >
        {content}
      </Box>
    </AnimatePresence>
  );
}

export default AnalyticsChartArea;
